import React, { useEffect, useState } from 'react';
import {
  createTurno,
  CreateTurnoPayload,
  getTurnos,
  Turno,
  updateTurno,
} from '../services/horarios.service';
import { getErrorMessage } from '../utils/errorHandler';
import { getDefaultValidezForHorario } from '../utils/turnoValidez';

const emptyForm: CreateTurnoPayload = {
  nombre: '',
  horaEntrada: '08:00',
  horaSalida: '15:00',
  inicioValidezEntrada: '07:30',
  finValidezEntrada: '08:30',
  inicioValidezSalida: '14:30',
  finValidezSalida: '16:00',
  toleranciaLlegadaTarde: 10,
  toleranciaSalidaTemprano: 0,
  rotativo: false,
  laborales: 1,
  noLaborales: 1,
  activo: true,
};

const cycleNumbers = [1, 2, 3, 4, 5, 6, 7];

const TurnosPage: React.FC = () => {
  const [turnos, setTurnos] = useState<Turno[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState<CreateTurnoPayload>(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await getTurnos();
      setTurnos(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void cargar();
  }, []);

  const onChange = <K extends keyof CreateTurnoPayload>(key: K, value: CreateTurnoPayload[K]) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };

      if (key === 'horaEntrada' && typeof value === 'string' && value) {
        const defaults = getDefaultValidezForHorario('entrada', value);
        return {
          ...next,
          ...defaults,
        };
      }

      if (key === 'horaSalida' && typeof value === 'string' && value) {
        const defaults = getDefaultValidezForHorario('salida', value);
        return {
          ...next,
          ...defaults,
        };
      }

      return next;
    });
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditId(null);
    setError('');
  };

  const onEdit = (turno: Turno) => {
    setEditId(turno._id);
    setForm({
      nombre: turno.nombre,
      horaEntrada: turno.horaEntrada,
      horaSalida: turno.horaSalida,
      inicioValidezEntrada: turno.inicioValidezEntrada || '',
      finValidezEntrada: turno.finValidezEntrada || '',
      inicioValidezSalida: turno.inicioValidezSalida || '',
      finValidezSalida: turno.finValidezSalida || '',
      toleranciaLlegadaTarde: turno.toleranciaLlegadaTarde,
      toleranciaSalidaTemprano: turno.toleranciaSalidaTemprano,
      rotativo: turno.rotativo,
      laborales: turno.laborales ?? 1,
      noLaborales: turno.noLaborales ?? 1,
      activo: turno.activo,
    });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      if (editId) {
        await updateTurno(editId, form);
        setSuccess('Turno actualizado correctamente');
      } else {
        await createTurno(form);
        setSuccess('Turno creado correctamente');
      }
      resetForm();
      await cargar();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <h1 className="page-title">Turnos - Turno</h1>

      <div className="card">
        <h2 className="card-title">{editId ? 'Editar turno' : 'Nuevo turno'}</h2>
        {error && <div className="error-msg">{error}</div>}
        {success && <div className="success-msg">{success}</div>}
        <form onSubmit={onSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Nombre turno</label>
              <input
                value={form.nombre}
                onChange={(e) => onChange('nombre', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Horario entrada</label>
              <input
                type="time"
                value={form.horaEntrada}
                onChange={(e) => onChange('horaEntrada', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Horario salida</label>
              <input
                type="time"
                value={form.horaSalida}
                onChange={(e) => onChange('horaSalida', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Inicio validez entrada</label>
              <input
                type="time"
                value={form.inicioValidezEntrada || ''}
                onChange={(e) => onChange('inicioValidezEntrada', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Fin validez entrada</label>
              <input
                type="time"
                value={form.finValidezEntrada || ''}
                onChange={(e) => onChange('finValidezEntrada', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Inicio validez salida</label>
              <input
                type="time"
                value={form.inicioValidezSalida || ''}
                onChange={(e) => onChange('inicioValidezSalida', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Fin validez salida</label>
              <input
                type="time"
                value={form.finValidezSalida || ''}
                onChange={(e) => onChange('finValidezSalida', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Tolerancia llegada tarde (min)</label>
              <input
                type="number"
                min={0}
                value={form.toleranciaLlegadaTarde ?? 0}
                onChange={(e) => onChange('toleranciaLlegadaTarde', Number(e.target.value))}
              />
            </div>
            <div className="form-group">
              <label>Tolerancia salida temprano (min)</label>
              <input
                type="number"
                min={0}
                value={form.toleranciaSalidaTemprano ?? 0}
                onChange={(e) => onChange('toleranciaSalidaTemprano', Number(e.target.value))}
              />
            </div>
          </div>

          <div className="checkbox-group">
            <input
              id="turno-rotativo"
              type="checkbox"
              checked={Boolean(form.rotativo)}
              onChange={(e) => {
                const checked = e.target.checked;
                onChange('rotativo', checked);
                if (checked) {
                  onChange('laborales', form.laborales ?? 1);
                  onChange('noLaborales', form.noLaborales ?? 1);
                }
              }}
            />
            <label htmlFor="turno-rotativo">Rotativo</label>
          </div>

          {form.rotativo && (
            <div className="form-grid" style={{ marginTop: '6px' }}>
              <div className="form-group">
                <label>Dias Activos</label>
                <select
                  value={String(form.laborales ?? 1)}
                  onChange={(e) => onChange('laborales', Number(e.target.value))}
                >
                  {cycleNumbers.map((n) => (
                    <option key={`lab-${n}`} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Descanso</label>
                <select
                  value={String(form.noLaborales ?? 1)}
                  onChange={(e) => onChange('noLaborales', Number(e.target.value))}
                >
                  {cycleNumbers.map((n) => (
                    <option key={`nol-${n}`} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="checkbox-group">
            <input
              id="turno-activo"
              type="checkbox"
              checked={Boolean(form.activo)}
              onChange={(e) => onChange('activo', e.target.checked)}
            />
            <label htmlFor="turno-activo">Activo</label>
          </div>

          <div className="actions-cell">
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Guardando...' : editId ? 'Guardar cambios' : 'Crear turno'}
            </button>
            {editId && (
              <button className="btn btn-outline" type="button" onClick={resetForm}>
                Cancelar edicion
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="card">
        <h2 className="card-title">Listado de turnos</h2>
        {loading ? (
          <div className="loading">Cargando...</div>
        ) : turnos.length === 0 ? (
          <div className="empty-state">No hay turnos registrados.</div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Entrada</th>
                  <th>Salida</th>
                  <th>Rotativo</th>
                  <th>Laborales</th>
                  <th>No laborales</th>
                  <th>Activo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {turnos.map((turno) => (
                  <tr key={turno._id}>
                    <td>{turno.nombre}</td>
                    <td>{turno.horaEntrada}</td>
                    <td>{turno.horaSalida}</td>
                    <td>{turno.rotativo ? 'Si' : 'No'}</td>
                    <td>{turno.rotativo ? (turno.laborales ?? '-') : '-'}</td>
                    <td>{turno.rotativo ? (turno.noLaborales ?? '-') : '-'}</td>
                    <td>{turno.activo ? 'Si' : 'No'}</td>
                    <td>
                      <button className="btn btn-outline btn-sm" type="button" onClick={() => onEdit(turno)}>
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default TurnosPage;
